import AuthService from './AuthService';

const API_BASE_URL =
  process.env.REACT_APP_VALIDATION_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  'http://localhost:8082/api';

// Helper function to get headers with auth
const getHeaders = (includeContentType = true) => {
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const authHeader = AuthService.getAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  return headers;
};

/**
 * Frontend Validation Service
 *
 * Fetches validation rules from backend and executes them locally for
 * immediate feedback. Rules with FRONTEND_ONLY or FULL_VALIDATION mode
 * can be executed locally. API_CALL rules always require backend.
 */
class ValidationService {
  // Cache for fetched rules (keyed by entityName:wizardKey)
  static rulesCache = new Map();

  /**
   * Fetch validation rules for a wizard step from backend.
   * Rules are cached to avoid repeated fetches.
   *
   * @param {string} entityName - Entity name (e.g., "PUTAWAY", "RECEIPT_LINE")
   * @param {string} wizardKey - Wizard key for context filtering
   * @returns {Promise<Object>} FrontendRulesResponse with rulesByField and lookups
   */
  static async fetchRulesForWizard(entityName, wizardKey) {
    const cacheKey = `${entityName}:${wizardKey || 'default'}`;

    // Return cached rules if available
    if (this.rulesCache.has(cacheKey)) {
      return this.rulesCache.get(cacheKey);
    }

    try {
      const url = wizardKey
        ? `${API_BASE_URL}/validate/frontend-rules/${encodeURIComponent(entityName)}/wizard/${encodeURIComponent(wizardKey)}`
        : `${API_BASE_URL}/validate/frontend-rules/${encodeURIComponent(entityName)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        console.warn(`Failed to fetch validation rules: ${response.status}`);
        return { rulesByField: {}, lookups: {} };
      }

      const rulesData = await response.json();
      this.rulesCache.set(cacheKey, rulesData);
      return rulesData;
    } catch (error) {
      console.error('Error fetching validation rules:', error);
      return { rulesByField: {}, lookups: {} };
    }
  }

  /**
   * Clear the rules cache (call when wizard changes or user logs out)
   */
  static clearCache() {
    this.rulesCache.clear();
  }

  /**
   * Validate a single field value against fetched rules.
   * Executes frontend-executable rules locally.
   *
   * @param {string} fieldName - Field name to validate
   * @param {*} value - Field value
   * @param {Object} rulesData - Fetched rules from fetchRulesForWizard
   * @param {string} triggerOn - Trigger event (ON_CHANGE, ON_SUBMIT)
   * @param {Object} context - Additional context data
   * @returns {Object} { valid: boolean, errors: Array<{ruleKey, message, severity}> }
   */
  static validateField(fieldName, value, rulesData, triggerOn = 'ON_CHANGE', context = {}) {
    const fieldRules = rulesData?.rulesByField?.[fieldName] || [];
    const lookups = rulesData?.lookups || {};

    const errors = [];

    for (const rule of fieldRules) {
      // Skip if rule is not for this trigger
      if (rule.triggerOn && rule.triggerOn !== triggerOn) {
        continue;
      }

      // Skip if rule is not frontend executable
      if (!rule.frontendExecutable) {
        continue;
      }

      const definition = rule.definition;
      let passed = true;
      let errorMessage = definition?.errorMessage || 'Validation failed';

      try {
        switch (definition?.validationType) {
          case 'EXPRESSION':
            passed = this.evaluateExpression(value, definition.expression, context);
            break;

          case 'REGEX':
            passed = this.evaluateRegex(value, definition.pattern, definition.allowNull);
            break;

          case 'RANGE':
            passed = this.evaluateRange(value, definition.min, definition.max);
            break;

          case 'LOOKUP':
            passed = this.evaluateLookup(value, definition.lookupCategory, lookups, definition.allowNull);
            break;

          case 'API_CALL':
            // API_CALL rules must be executed on backend
            continue;

          default:
            // Unknown type, skip
            continue;
        }
      } catch (e) {
        console.warn(`Validation error for rule ${rule.ruleKey}:`, e);
        passed = false;
        errorMessage = `Validation error: ${e.message}`;
      }

      if (!passed) {
        // Interpolate error message
        errorMessage = this.interpolateMessage(errorMessage, value, context);

        errors.push({
          ruleKey: rule.ruleKey,
          message: errorMessage,
          severity: definition?.severity || 'ERROR',
          errorCode: definition?.errorCode
        });

        // Stop on first ERROR
        if (definition?.severity === 'ERROR') {
          break;
        }
      }
    }

    return {
      valid: errors.length === 0 || errors.every(e => e.severity !== 'ERROR'),
      errors
    };
  }

  /**
   * Validate all fields at once (e.g., on form submit)
   *
   * @param {Object} fieldValues - Map of fieldId to value
   * @param {Object} rulesData - Fetched rules
   * @param {string} triggerOn - Trigger event
   * @param {Object} context - Additional context
   * @returns {Object} { valid: boolean, errorsByField: { [fieldId]: errors } }
   */
  static validateAllFields(fieldValues, rulesData, triggerOn = 'ON_SUBMIT', context = {}) {
    const errorsByField = {};
    let allValid = true;

    for (const [fieldId, value] of Object.entries(fieldValues)) {
      const result = this.validateField(fieldId, value, rulesData, triggerOn, context);

      if (!result.valid) {
        allValid = false;
      }

      if (result.errors.length > 0) {
        errorsByField[fieldId] = result.errors;
      }
    }

    return {
      valid: allValid,
      errorsByField
    };
  }

  /**
   * Call backend validation for backend-only rules.
   * Use this on form submit after frontend validation passes.
   *
   * @param {Object} request - Validation request
   * @returns {Promise<Object>} ValidationResult from backend
   */
  static async validateOnBackend(request) {
    try {
      const response = await fetch(`${API_BASE_URL}/validate/field/backend-only`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        console.warn(`Backend validation failed: ${response.status}`);
        return { valid: true, ruleResults: [] }; // Fail open if backend unavailable
      }

      return await response.json();
    } catch (error) {
      console.error('Backend validation error:', error);
      return { valid: true, ruleResults: [] }; // Fail open
    }
  }

  // =========================================================================
  // Validation Type Implementations
  // =========================================================================

  /**
   * Evaluate an EXPRESSION rule.
   * Supports: value > 0, value >= 1, value != null, value.length > 0
   */
  static evaluateExpression(value, expression, context = {}) {
    if (!expression) return true;

    expression = expression.trim();

    // Handle null checks
    if (expression === 'value != null' || expression === 'value !== null') {
      return value != null && value !== '';
    }
    if (expression === 'value == null' || expression === 'value === null') {
      return value == null || value === '';
    }

    if (value == null || value === '') {
      return false; // Can't evaluate expressions on empty values
    }

    // Handle string length checks
    if (expression.includes('value.length')) {
      const strValue = String(value);
      expression = expression.replace('value.length', String(strValue.length));
      return this.evaluateNumericExpression(expression, context);
    }

    // Handle numeric comparisons
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return false;
    }

    expression = expression.replace('value', String(numValue));
    return this.evaluateNumericExpression(expression, context);
  }

  /**
   * Evaluate simple numeric expressions.
   */
  static evaluateNumericExpression(expression, context = {}) {
    // Replace context variables
    for (const [key, val] of Object.entries(context)) {
      const placeholder = `context.${key}`;
      if (expression.includes(placeholder) && val != null) {
        expression = expression.replace(placeholder, String(val));
      }
    }

    // Parse and evaluate
    if (expression.includes('>=')) {
      const parts = expression.split('>=');
      return parseFloat(parts[0]) >= parseFloat(parts[1]);
    }
    if (expression.includes('<=')) {
      const parts = expression.split('<=');
      return parseFloat(parts[0]) <= parseFloat(parts[1]);
    }
    if (expression.includes('!=')) {
      const parts = expression.split('!=');
      return parseFloat(parts[0]) !== parseFloat(parts[1]);
    }
    if (expression.includes('==')) {
      const parts = expression.split('==');
      return parseFloat(parts[0]) === parseFloat(parts[1]);
    }
    if (expression.includes('>')) {
      const parts = expression.split('>');
      return parseFloat(parts[0]) > parseFloat(parts[1]);
    }
    if (expression.includes('<')) {
      const parts = expression.split('<');
      return parseFloat(parts[0]) < parseFloat(parts[1]);
    }

    return true;
  }

  /**
   * Evaluate a REGEX rule.
   */
  static evaluateRegex(value, pattern, allowNull = false) {
    if (!pattern) return true;

    if (value == null || value === '') {
      return allowNull === true;
    }

    try {
      const regex = new RegExp(pattern);
      return regex.test(String(value));
    } catch (e) {
      console.warn('Invalid regex pattern:', pattern, e);
      return true;
    }
  }

  /**
   * Evaluate a RANGE rule.
   */
  static evaluateRange(value, min, max) {
    if (value == null || value === '') {
      return false;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return false;
    }

    if (min != null && numValue < min) {
      return false;
    }
    if (max != null && numValue > max) {
      return false;
    }

    return true;
  }

  /**
   * Evaluate a LOOKUP rule - value must exist in lookup values.
   */
  static evaluateLookup(value, lookupCategory, lookups, allowNull = false) {
    if (value == null || value === '') {
      return allowNull === true;
    }

    const allowedValues = lookups[lookupCategory];
    if (!allowedValues || !Array.isArray(allowedValues)) {
      // No lookup data available, pass by default
      return true;
    }

    return allowedValues.includes(String(value));
  }

  /**
   * Interpolate placeholders in error message.
   */
  static interpolateMessage(message, value, context = {}) {
    if (!message) return message;

    // Replace {{value}}
    if (value != null) {
      message = message.replace('{{value}}', String(value));
    }

    // Replace {{context.xxx}}
    for (const [key, val] of Object.entries(context)) {
      const placeholder = `{{context.${key}}}`;
      if (message.includes(placeholder) && val != null) {
        message = message.replace(placeholder, String(val));
      }
    }

    return message;
  }
}

export default ValidationService;
